import { createFileRoute, Link } from "@tanstack/react-router";
import { ContentPage, ContentSection, BiText } from "@/components/layout/ContentPage";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/why")({
  head: () => {
    const seo = buildSeo({
      title: "Why Bangla Calculator — BD-First with Islamic Suite",
      description:
        "Why we built Bangla Calculator: katha-bigha land, NBR tax, BD bank EMI, Bengali calendar, plus Zakat, Prayer Times, Faraid, Qibla for BD Muslims.",
      path: "/why",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Why Bangla Calculator", path: "/why" },
      ],
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: WhyPage,
});

function WhyPage() {
  return (
    <ContentPage
      titleEn="Why Bangla Calculator"
      titleBn="কেন বাংলা ক্যালকুলেটর"
      introEn="Most online calculators are built for the US, India, or UK — not Bangladesh. Here's what we do differently, and why we built a complete Islamic suite alongside the secular tools."
      introBn="বেশিরভাগ অনলাইন ক্যালকুলেটর বানানো হয়েছে আমেরিকা, ভারত বা যুক্তরাজ্যের জন্য — বাংলাদেশের জন্য নয়। আমরা কীভাবে আলাদা, এবং কেন ধর্মনিরপেক্ষ টুলগুলোর পাশাপাশি একটি সম্পূর্ণ ইসলামিক স্যুট তৈরি করেছি — তা এখানে।"
    >
      <ContentSection headingEn="1. The problem with generic calculators" headingBn="১. সাধারণ ক্যালকুলেটরের সমস্যা">
        <BiText
          en={
            <>
              <p><strong>Secular gaps:</strong></p>
              <ul className="ml-5 list-disc space-y-1">
                <li>No support for BD-specific units (katha, bigha, mon, sher, tola, vori)</li>
                <li>No NBR tax slabs, no BD bank rates, no DPS/Sanchaypatra</li>
                <li>No Bengali calendar or numerals</li>
                <li>Asian-specific BMI standards ignored</li>
                <li>Casio FX-991ES Plus emulators are buggy or paywalled</li>
              </ul>
              <p className="mt-3"><strong>Islamic gaps:</strong></p>
              <ul className="ml-5 list-disc space-y-1">
                <li>Foreign Zakat calculators don't show BDT or vori</li>
                <li>No BD assets like DPS, FDR, Sanchaypatra, bKash</li>
                <li>Don't follow Hanafi madhhab default</li>
                <li>Prayer time calculators don't use Karachi method (BD standard)</li>
                <li>Limited Bengali support; Faraid calculators are mostly Arabic or English-only</li>
              </ul>
            </>
          }
          bn={
            <>
              <p><strong>ধর্মনিরপেক্ষ ঘাটতি:</strong></p>
              <ul className="ml-5 list-disc space-y-1">
                <li>বাংলাদেশি একক (কাঠা, বিঘা, মণ, সের, তোলা, ভরি) সাপোর্ট নেই</li>
                <li>NBR কর স্ল্যাব নেই, বাংলাদেশি ব্যাংকের হার নেই, DPS/সঞ্চয়পত্র নেই</li>
                <li>বাংলা ক্যালেন্ডার বা সংখ্যা নেই</li>
                <li>এশীয় BMI মানদণ্ড উপেক্ষিত</li>
                <li>Casio FX-991ES Plus এমুলেটরগুলো বাগি অথবা পেওয়াল-আবদ্ধ</li>
              </ul>
              <p className="mt-3"><strong>ইসলামিক ঘাটতি:</strong></p>
              <ul className="ml-5 list-disc space-y-1">
                <li>বিদেশি যাকাত ক্যালকুলেটরে BDT বা ভরি নেই</li>
                <li>DPS, FDR, সঞ্চয়পত্র, বিকাশের মতো বাংলাদেশি অ্যাসেট নেই</li>
                <li>হানাফি মাযহাব ডিফল্ট নয়</li>
                <li>নামাজের সময়ে কারাচি পদ্ধতি (বাংলাদেশের মান) নেই</li>
                <li>সীমিত বাংলা সাপোর্ট; ফরায়েজ ক্যালকুলেটর বেশিরভাগ আরবি বা শুধু ইংরেজি</li>
              </ul>
            </>
          }
        />
      </ContentSection>

      <ContentSection headingEn="2. Our approach" headingBn="২. আমাদের দৃষ্টিভঙ্গি">
        <ul className="ml-5 list-disc space-y-1.5">
          <BiText
            en={
              <>
                <li><strong>BD-first:</strong> every calculator considers BD context first</li>
                <li><strong>Bilingual:</strong> English ↔ বাংলা on every page</li>
                <li><strong>Free:</strong> every calculator, every feature, no paywall</li>
                <li><strong>No signup:</strong> just visit and use</li>
                <li><strong>Privacy-first:</strong> runs in your browser; we don't collect your calculations — especially important for personal financial and religious data</li>
                <li><strong>Comprehensive:</strong> 51+ calculators in one place</li>
                <li><strong>Religiously accurate:</strong> Islamic calculators reviewed by qualified scholars; mainstream Hanafi consensus; clear disclaimers for edge cases</li>
              </>
            }
            bn={
              <>
                <li><strong>বাংলাদেশ-প্রথম:</strong> প্রতিটি ক্যালকুলেটরে আগে বাংলাদেশের প্রেক্ষাপট</li>
                <li><strong>দ্বিভাষিক:</strong> প্রতিটি পেজে ইংরেজি ↔ বাংলা</li>
                <li><strong>বিনামূল্যে:</strong> প্রতিটি ক্যালকুলেটর, প্রতিটি ফিচার, কোনো পেওয়াল নেই</li>
                <li><strong>সাইন-আপ নেই:</strong> এসেই ব্যবহার করুন</li>
                <li><strong>প্রাইভেসি-প্রথম:</strong> আপনার ব্রাউজারে চলে; আমরা আপনার হিসাব সংগ্রহ করি না — ব্যক্তিগত আর্থিক ও ধর্মীয় ডাটার জন্য বিশেষ গুরুত্বপূর্ণ</li>
                <li><strong>সম্পূর্ণ:</strong> এক জায়গায় ৫১+ ক্যালকুলেটর</li>
                <li><strong>ধর্মীয়ভাবে নির্ভুল:</strong> ইসলামিক ক্যালকুলেটর যোগ্য আলেমদের পর্যালোচিত; মূলধারার হানাফি ঐকমত্য; বিশেষ ক্ষেত্রে স্পষ্ট দাবিত্যাগ</li>
              </>
            }
          />
        </ul>
      </ContentSection>

      <ContentSection headingEn="3. What you get" headingBn="৩. আপনি কী পান">
        <h3 className="font-semibold text-foreground"><BiText en="For everyone" bn="সবার জন্য" /></h3>
        <ul className="ml-5 list-disc space-y-1">
          <BiText
            en={
              <>
                <li><Link to="/land-calculator" className="text-accent underline underline-offset-2">Land Calculator</Link> with all BD units (katha, bigha, shotok, decimal, kani, gonda + sq ft, sq meter, acre)</li>
                <li><Link to="/emi-calculator" className="text-accent underline underline-offset-2">EMI Calculator</Link> with DBBL, BRAC, City Bank, EBL presets</li>
                <li><Link to="/tax-calculator" className="text-accent underline underline-offset-2">Income Tax Calculator</Link> with current FY 2025-26 NBR slabs and investment rebate</li>
                <li><Link to="/duty-calculator" className="text-accent underline underline-offset-2">Customs Duty Calculator</Link> with full BD stack: CD, SD, VAT, AIT, RD</li>
                <li><Link to="/compound-interest-calculator" className="text-accent underline underline-offset-2">Compound Interest</Link> with DPS, FDR, Sanchaypatra presets</li>
                <li>Bengali calendar (Bangla shon) and Hijri across date calculators</li>
                <li><Link to="/casio-fx-991es" className="text-accent underline underline-offset-2">Casio FX-991ES Plus</Link> and <Link to="/casio-fx-991ex" className="text-accent underline underline-offset-2">FX-991EX</Link> emulators</li>
                <li><Link to="/gpa-calculator" className="text-accent underline underline-offset-2">GPA Calculator</Link> with SSC, HSC, DU, BUET, NU, private university scales</li>
                <li><Link to="/bmi-calculator" className="text-accent underline underline-offset-2">BMI</Link> with South Asian (Asian) thresholds, not WHO global</li>
              </>
            }
            bn={
              <>
                <li><Link to="/land-calculator" className="text-accent underline underline-offset-2">জমি ক্যালকুলেটর</Link> — সব বাংলাদেশি একক (কাঠা, বিঘা, শতক, ডেসিমাল, কানি, গণ্ডা + বর্গফুট, বর্গমিটার, একর)</li>
                <li><Link to="/emi-calculator" className="text-accent underline underline-offset-2">EMI ক্যালকুলেটর</Link> — DBBL, BRAC, City Bank, EBL প্রিসেট</li>
                <li><Link to="/tax-calculator" className="text-accent underline underline-offset-2">আয়কর ক্যালকুলেটর</Link> — চলতি অর্থবছর ২০২৫-২৬ NBR স্ল্যাব ও ইনভেস্টমেন্ট রিবেট</li>
                <li><Link to="/duty-calculator" className="text-accent underline underline-offset-2">কাস্টমস ডিউটি ক্যালকুলেটর</Link> — সম্পূর্ণ বাংলাদেশি স্ট্যাক: CD, SD, VAT, AIT, RD</li>
                <li><Link to="/compound-interest-calculator" className="text-accent underline underline-offset-2">চক্রবৃদ্ধি সুদ</Link> — DPS, FDR, সঞ্চয়পত্র প্রিসেট</li>
                <li>তারিখ ক্যালকুলেটরে বাংলা সন ও হিজরি</li>
                <li><Link to="/casio-fx-991es" className="text-accent underline underline-offset-2">Casio FX-991ES Plus</Link> ও <Link to="/casio-fx-991ex" className="text-accent underline underline-offset-2">FX-991EX</Link> এমুলেটর</li>
                <li><Link to="/gpa-calculator" className="text-accent underline underline-offset-2">GPA ক্যালকুলেটর</Link> — SSC, HSC, DU, BUET, NU, প্রাইভেট বিশ্ববিদ্যালয় স্কেল</li>
                <li><Link to="/bmi-calculator" className="text-accent underline underline-offset-2">BMI</Link> — দক্ষিণ এশীয় (এশীয়) থ্রেশহোল্ড, WHO গ্লোবাল নয়</li>
              </>
            }
          />
        </ul>

        <h3 className="mt-4 font-semibold text-foreground"><BiText en="For Bangladeshi Muslims" bn="বাংলাদেশি মুসলিমদের জন্য" /></h3>
        <ul className="ml-5 list-disc space-y-1">
          <BiText
            en={
              <>
                <li><Link to="/zakat-calculator" className="text-accent underline underline-offset-2"><strong>Zakat Calculator</strong></Link> — vori support, BDT prices, DPS/FDR/Sanchaypatra/bKash inclusion, Hanafi default, all 8 Qur'anic recipient categories</li>
                <li><Link to="/prayer-times" className="text-accent underline underline-offset-2"><strong>Prayer Times</strong></Link> — all 64 districts, Karachi method, Hanafi Asr, Hijri-Bengali-Gregorian three-way display</li>
                <li><Link to="/sehri-iftar-time" className="text-accent underline underline-offset-2"><strong>Sehri &amp; Iftar Time</strong></Link> — full Ramadan calendar, live Iftar countdown, niyyat and dua</li>
                <li><Link to="/hijri-date-converter" className="text-accent underline underline-offset-2"><strong>Hijri Date Converter</strong></Link> — three-way (Gregorian-Hijri-Bengali), Islamic events calendar</li>
                <li><Link to="/faraid-calculator" className="text-accent underline underline-offset-2"><strong>Faraid Calculator</strong></Link> — Hanafi default, Qur'anic citations, classical case handling (Awl, Radd, Hajb), Bengali translations, money + land</li>
                <li><Link to="/qibla-direction" className="text-accent underline underline-offset-2"><strong>Qibla Direction</strong></Link> — all 64 BD districts precomputed, live compass, true bearing</li>
              </>
            }
            bn={
              <>
                <li><Link to="/zakat-calculator" className="text-accent underline underline-offset-2"><strong>যাকাত ক্যালকুলেটর</strong></Link> — ভরি সাপোর্ট, BDT মূল্য, DPS/FDR/সঞ্চয়পত্র/বিকাশ অন্তর্ভুক্তি, হানাফি ডিফল্ট, কুরআনের ৮ প্রাপক ক্যাটাগরি</li>
                <li><Link to="/prayer-times" className="text-accent underline underline-offset-2"><strong>নামাজের সময়</strong></Link> — ৬৪ জেলা, কারাচি পদ্ধতি, হানাফি আসর, হিজরি-বাংলা-গ্রেগরিয়ান তিন-পদ্ধতি</li>
                <li><Link to="/sehri-iftar-time" className="text-accent underline underline-offset-2"><strong>সেহরি ও ইফতার সময়</strong></Link> — সম্পূর্ণ রমজান ক্যালেন্ডার, লাইভ ইফতার কাউন্টডাউন, নিয়ত ও দোয়া</li>
                <li><Link to="/hijri-date-converter" className="text-accent underline underline-offset-2"><strong>হিজরি কনভার্টার</strong></Link> — তিন-পদ্ধতি (গ্রেগরিয়ান-হিজরি-বাংলা), ইসলামিক ইভেন্ট ক্যালেন্ডার</li>
                <li><Link to="/faraid-calculator" className="text-accent underline underline-offset-2"><strong>ফরায়েজ ক্যালকুলেটর</strong></Link> — হানাফি ডিফল্ট, কুরআনের রেফারেন্স, ক্লাসিক্যাল কেস (আউল, রদ্দ, হাজব), বাংলা অনুবাদ, টাকা ও জমি</li>
                <li><Link to="/qibla-direction" className="text-accent underline underline-offset-2"><strong>কিবলা দিক</strong></Link> — ৬৪ জেলা প্রি-কম্পিউটেড, লাইভ কম্পাস, ট্রু বেয়ারিং</li>
              </>
            }
          />
        </ul>
      </ContentSection>

      <ContentSection headingEn="4. Built by Bangladeshis, for Bangladeshis" headingBn="৪. বাংলাদেশিদের দ্বারা, বাংলাদেশিদের জন্য">
        <BiText
          en={<p>Including the Muslim community — with the same care and rigor we'd want for ourselves and our families.</p>}
          bn={<p>মুসলিম সম্প্রদায় সহ — যে যত্ন ও নিষ্ঠা আমরা নিজেদের ও পরিবারের জন্য চাই, একই মানে।</p>}
        />
      </ContentSection>

      <ContentSection headingEn="5. Free forever pledge" headingBn="৫. চিরকাল বিনামূল্যে অঙ্গীকার">
        <BiText
          en={<p>We commit to keeping every calculator free. We will <strong>never paywall the Islamic suite</strong> — religious tools should always be free.</p>}
          bn={<p>প্রতিটি ক্যালকুলেটর বিনামূল্যে রাখার প্রতিশ্রুতি। <strong>ইসলামিক স্যুট আমরা কখনো পেওয়াল করব না</strong> — ধর্মীয় টুল সর্বদা বিনামূল্যে থাকা উচিত।</p>}
        />
      </ContentSection>

      <ContentSection headingEn="6. Religious credibility commitment" headingBn="৬. ধর্মীয় বিশ্বস্ততার অঙ্গীকার">
        <BiText
          en={
            <p>
              We treat Islamic content with the rigor it deserves. Our Islamic calculators are reviewed by qualified Bangladeshi scholars before launch.
              We follow mainstream Hanafi consensus, cite Qur'anic and Hadith references, and include clear disclaimers for edge cases that require scholarly consultation.
              If you're a Mufti or Islamic scholar and notice an error, please <Link to="/contact" className="text-accent underline underline-offset-2">contact us</Link> —
              corrections are reviewed within 48 hours.
            </p>
          }
          bn={
            <p>
              ইসলামিক কনটেন্টকে আমরা প্রাপ্য নিষ্ঠার সাথে দেখি। আমাদের ইসলামিক ক্যালকুলেটরগুলো লঞ্চের আগে যোগ্য বাংলাদেশি আলেমদের দ্বারা পর্যালোচিত।
              আমরা মূলধারার হানাফি ঐকমত্য অনুসরণ করি, কুরআন ও হাদিসের রেফারেন্স উল্লেখ করি, এবং আলেমের পরামর্শ প্রয়োজন এমন বিশেষ ক্ষেত্রে স্পষ্ট দাবিত্যাগ অন্তর্ভুক্ত করি।
              আপনি মুফতি বা আলেম হলে এবং কোনো ভুল লক্ষ্য করলে অনুগ্রহ করে <Link to="/contact" className="text-accent underline underline-offset-2">আমাদের সাথে যোগাযোগ করুন</Link> —
              সংশোধন ৪৮ ঘণ্টার মধ্যে পর্যালোচিত হয়।
            </p>
          }
        />
      </ContentSection>
    </ContentPage>
  );
}
